import { z } from "zod";
import { BaseFirestoreSchema } from "./firestore.schema";
import { UploadTypeIdSchema } from "./document.schema";

export const IntegrationSchema = BaseFirestoreSchema.extend({});

////////////////////////////////////////////
// Laserfiche Integration Schemas
////////////////////////////////////////////

// details for connecting to a Laserfiche account
export const LaserficheConnectionSchema = z.discriminatedUnion("hostingType", [
  z.object({
    hostingType: z.literal("self"),
    baseUrl: z.string(),
    repository: z.string(),
    nickname: z.string().optional(), // optional
    username: z.string(), // encrypted
    password: z.string(), // encrypted
  }),
  z.object({
    hostingType: z.literal("cloud"),
    repository: z.string(),
    nickname: z.string().optional(), // optional
    servicePrincipalKey: z.string(),
    accessKey: z.string(),
  }),
]);

// indicates a directory in a Laserfiche repository
export const LaserficheDirectorySchema = z.object({
  directoryId: z.number(), // source of truth
  directoryPath: z.string(), // for display (actual directory names might change)
});

// specifies a Laserfiche tag
export const LaserficheTagSchema = z.object({
  tagId: z.number(), // source of truth
  tagName: z.string(), // for display (actual name might change)
});

// specifies a Laserfiche field
export const LaserficheFieldSchema = z.object({
  fieldId: z.number(), // source of truth
  fieldName: z.string(), // for display (actual name might change)
  sourceKey: z.string(), // where this field's value is pulled from in CiviDocs
  constantValue: z.string().optional(), // when source key is empty, this raw value will be applied to the field
});

// specifies a Laserfiche template and its associated fields
export const LaserficheTemplateSchema = z.object({
  templateId: z.number(), // source of truth
  templateName: z.string(), // for display (actual name might change)
  templateFieldAssignments: LaserficheFieldSchema.array(), // field assignments applied
});

// metadata to associate with a certain set of files
export const LaserficheMetadataSchema = z.object({
  enabled: z.boolean(),
  tags: LaserficheTagSchema.array(),
  fields: LaserficheFieldSchema.array(),
  template: LaserficheTemplateSchema.nullish(),
});

// configuration internal to CiviDocs for this Laserfiche connection
export const LaserficheInternalConfigurationSchema = z.object({
  errorEmail: z.string().optional(),
});

// full details for representing a single Laserfiche account integration
export const LaserficheConfigurationSchema = z.object({
  connection: LaserficheConnectionSchema.optional(),
  output: z.object({
    primary: LaserficheDirectorySchema.optional(),
    orphanSigned: LaserficheDirectorySchema.optional(),
  }),
  metadata: z.object({
    agendas: LaserficheMetadataSchema.optional(),
    documents: LaserficheMetadataSchema.optional(),
    minutes: LaserficheMetadataSchema.optional(),
    signed: LaserficheMetadataSchema.optional(),
    orphanSigned: LaserficheMetadataSchema.optional(),
    documentType: z.record(LaserficheMetadataSchema).optional(),
  }),
  internal: LaserficheInternalConfigurationSchema.optional(),
});

// actual integration definition
export const IntegrationLaserficheSchema = IntegrationSchema.extend({
  tenantConfig: LaserficheConfigurationSchema.nullish(),
  teamConfigs: z.record(LaserficheConfigurationSchema).nullish(),
});

// Laserfiche DTO elements

// types of documents pushed to Laserfiche
export const LaserfichePushTypeSchema =
  LaserficheConfigurationSchema.shape.metadata
    .keyof()
    .exclude(["documentType"]);

export const LaserficheConnectionDtoSchema = z.discriminatedUnion(
  "hostingType",
  [
    LaserficheConnectionSchema.options[0].omit({
      username: true,
      password: true,
    }),
    LaserficheConnectionSchema.options[1].omit({
      servicePrincipalKey: true,
      accessKey: true,
    }),
  ]
);

// type for a single entry returned in a directory listing
export const LaserficheInspectDirectoryItemSchema = z.object({
  name: z.string(),
  id: z.number(),
  type: z.enum(["file", "folder"]),
  parentId: z.number().optional(),
  path: z.string(),
});

// type for a tag returned in an available tag listing
export const LaserficheInspectTagSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().optional(),
});

// type for a field returned in an available field listing
export const LaserficheInspectFieldSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  description: z.string().optional(),
  length: z.number().optional(),
  constraint: z.string().optional(),
});

// type for a template returned in an available template listing
export const LaserficheInspectTemplateSchema = z.object({
  id: z.number(),
  name: z.string(),
  fields: z.number().array(),
  description: z.string().optional(),
});

// type when updating directory config
export const LaserfichePutDirectorySchema = LaserficheDirectorySchema.omit({
  directoryPath: true,
});

// type when updating a tag config
export const LaserfichePutTagSchema = LaserficheTagSchema.omit({
  tagName: true,
});

// type when updating a field config
export const LaserfichePutFieldSchema = LaserficheFieldSchema.omit({
  fieldName: true,
});

// type when updating a template config
export const LaserfichePutTemplateSchema = LaserficheTemplateSchema.omit({
  templateName: true,
  templateFieldAssignments: true,
}).extend({
  templateFieldAssignments: LaserfichePutFieldSchema.array(),
});

// type when updating a metadata config
export const LaserfichePutMetadataSchema = LaserficheMetadataSchema.omit({
  tags: true,
  template: true,
  fields: true,
})
  .partial()
  .extend({
    tags: LaserfichePutTagSchema.array().optional(),
    template: LaserfichePutTemplateSchema.nullable().optional(),
    fields: LaserfichePutFieldSchema.array().optional(),
  });

export const LaserficheMetadataConfigKeySchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("basic"),
    subType: LaserfichePushTypeSchema,
  }),
  z.object({
    type: z.literal("documentType"),
    subType: UploadTypeIdSchema,
  }),
]);

const meetingFields = [
  "meetingName",
  "meetingDescription",
  "meetingTeam",
  "meetingType",
  "meetingDate",
  "meetingTags",
  "meetingAgendaItemTags",
  "meetingAttachmentNames",
  "meetingPublishDate",
  "meetingAddress",
  "meetingVersion",
  "meetingCreatedAt",
  "meetingModifiedAt",
  "meetingCreatedBy",
  "meetingModifiedBy",
  "softwareVersion",
] as const;

const documentFields = [
  "documentName",
  "documentDescription",
  "documentTeam",
  "documentTags",
  "documentUrl",
  "documentFileName",
  "documentFileSize",
  "documentUploadType",
  "documentVersion",
  "documentCreatedAt",
  "documentModifiedAt",
  "documentCreatedBy",
  "documentModifiedBy",
] as const;

const minutesFields = ["minutesRevision", "minutesOfficial"] as const;

export const LaserfichePushTypeFieldSourcesSchema = z.object({
  agendas: z.enum([...meetingFields]),
  documents: z.enum([...documentFields, ...meetingFields]),
  minutes: z.enum([...documentFields, ...minutesFields, ...meetingFields]),
  signed: z.enum([...documentFields, ...meetingFields]),
  orphanSigned: z.enum([...documentFields]),
});

export const LaserfichePushTypeAllFieldSourcesSchema = z.enum([
  ...documentFields,
  ...minutesFields,
  ...meetingFields,
]);
